import { SessionCache } from '@/lib/session-cache'
import { supabase } from '@/lib/supabase'

// Check if token is a demo token
function isDemoToken(token: string | undefined): boolean {
  return token === 'demo-token'
}

// Try to get demo session from localStorage directly
function getDemoSessionFromStorage(): { access_token: string } | null {
  if (typeof window === 'undefined') return null
  try {
    const cached = localStorage.getItem('supabase.auth.session')
    if (cached) {
      const parsed = JSON.parse(cached)
      if (parsed?.access_token === 'demo-token') {
        return parsed
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return null
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  let session = SessionCache.getSession()
  
  // If no session from cache, check if there's a demo session in localStorage
  // (SessionCache might have cleared memoryCache but localStorage still has it)
  if (!session && typeof window !== 'undefined') {
    const demoSession = getDemoSessionFromStorage()
    if (demoSession) {
      session = demoSession as any
    }
  }
  
  // Only try Supabase getSession if we still don't have a session and it's not demo
  if (!session && typeof window !== 'undefined') {
    try {
      const { data } = await supabase.auth.getSession()
      session = data.session ?? null
    } catch (e) {
      // Ignore Supabase errors
    }
  }
  
  // Only attempt refresh for non-demo sessions
  const isDemo = isDemoToken(session?.access_token)
  if (!isDemo && session?.expires_at && session.expires_at * 1000 < Date.now() + 30000) {
    try {
      const { data } = await supabase.auth.refreshSession()
      session = data.session ?? session
    } catch (e) {
      // If refresh fails, continue with existing session
      console.warn('[authFetch] Session refresh failed, using existing session')
    }
  }
  
  const headers = new Headers(init.headers || {})

  if (session?.access_token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${session.access_token}`)
  }

  return fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  })
}
